import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  CalendarX2,
  Gift,
  Pencil,
  Power,
  PowerOff,
  ScrollText,
  UserPlus,
  Users,
} from 'lucide-react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Drawer } from '../../components/Drawer';
import {
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  Field,
  Pagination,
  Row,
  Skeleton,
  Textarea,
} from '../../components/ui';
import { api } from '../../lib/api';
import { formatCredits, formatDateTime, formatNumber } from '../../lib/format';
import { useT } from '../../lib/i18n';
import { errorMessage, useToast } from '../../lib/toast';
import type {
  AddCampaignMembersResult,
  Paginated,
  ReferralCampaign,
  ReferralCodeRow,
} from '../../lib/types';
import { CAMPAIGN_TONES, describeOffer, formatReward, parseDeviceIds, UserRef } from './shared';

type Action = 'end' | 'archive' | 'members';

/**
 * One campaign: the offer, how it is doing, and everyone holding a code.
 *
 * The actions are the ones an operator reaches for, in the order they are
 * reached for — edit a typo, take someone out, add someone, end it early —
 * and archiving sits apart in red, because it is the one that also stops
 * paying for friends already on their way.
 */
export function CampaignDrawer({
  campaignId,
  onClose,
  onEdit,
  onShow,
}: {
  campaignId: string;
  onClose: () => void;
  onEdit: (campaign: ReferralCampaign) => void;
  /** Opens another tab filtered to this campaign. */
  onShow: (tab: 'redemptions' | 'rewards' | 'activity', campaignId: string) => void;
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [action, setAction] = useState<Action | null>(null);
  const [memberText, setMemberText] = useState('');

  const campaign = useQuery({
    queryKey: ['referrals', 'campaign', campaignId],
    queryFn: () => api.get<ReferralCampaign>(`/api/referrals/campaigns/${campaignId}`),
  });

  const members = useMemo(() => parseDeviceIds(memberText), [memberText]);

  const act = useMutation({
    mutationFn: async ({ kind, reason }: { kind: Action; reason: string }) => {
      if (kind === 'end') {
        await api.post(`/api/referrals/campaigns/${campaignId}/end`, { reason });
        return null;
      }
      if (kind === 'archive') {
        await api.delete(`/api/referrals/campaigns/${campaignId}`, { reason });
        return null;
      }
      return api.post<AddCampaignMembersResult>(
        `/api/referrals/campaigns/${campaignId}/members`,
        { deviceIds: members.valid, reason },
      );
    },
    onSuccess: (result, { kind }) => {
      void queryClient.invalidateQueries({ queryKey: ['referrals'] });
      setAction(null);
      if (kind === 'members' && result) {
        setMemberText('');
        toast.success(
          t('referrals.drawer.membersAdded', {
            added: result.added,
            reactivated: result.reactivated,
            already: result.alreadyMembers,
          }),
        );
        return;
      }
      toast.success(t(`referrals.drawer.done.${kind}`));
      if (kind === 'archive') onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const data = campaign.data;

  return (
    <Drawer
      open
      onClose={onClose}
      title={data?.name ?? t('referrals.drawer.loading')}
      footer={
        data ? (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              className="flex-1"
              icon={<Pencil size={15} />}
              onClick={() => onEdit(data)}
            >
              {t('referrals.drawer.edit')}
            </Button>
            {data.status !== 'ended' && (
              <Button
                className="flex-1"
                icon={<CalendarX2 size={15} />}
                onClick={() => setAction('end')}
              >
                {t('referrals.drawer.end')}
              </Button>
            )}
            <Button
              variant="danger"
              icon={<Archive size={15} />}
              onClick={() => setAction('archive')}
              aria-label={t('referrals.drawer.archive')}
            >
              <span className="hidden sm:inline">{t('referrals.drawer.archive')}</span>
            </Button>
          </div>
        ) : undefined
      }
    >
      {campaign.isError && <ErrorNote error={campaign.error} />}
      {campaign.isPending || !data ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="space-y-5">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={CAMPAIGN_TONES[data.status]}>
                {t(`referrals.status.${data.status}`)}
              </Badge>
              <Badge tone={data.audience === 'users' ? 'brand' : 'neutral'}>
                {t(`referrals.audience.${data.audience}`)}
              </Badge>
              {data.termsLocked && <Badge>{t('referrals.editor.termsLocked')}</Badge>}
            </div>
            <p className="text-sm font-medium text-ink">{describeOffer(data)}</p>
            {data.description && (
              <p className="text-xs whitespace-pre-line text-ink-2">{data.description}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Stat label={t('referrals.stat.joined')} value={formatNumber(data.stats.joined)} />
            <Stat
              label={t('referrals.stat.qualified')}
              value={formatNumber(data.stats.qualified)}
            />
            <Stat
              label={t('referrals.stat.pending')}
              value={formatNumber(data.stats.pending)}
            />
            <Stat
              label={t('referrals.stat.rewards')}
              value={formatNumber(data.stats.rewardsGranted)}
            />
            <Stat
              label={t('referrals.stat.paid')}
              value={
                data.rewardType === 'free_trial'
                  ? t('referrals.reward.trialDays', { count: data.stats.trialDaysGranted })
                  : formatCredits(data.stats.daylightsGranted)
              }
            />
            <Stat
              label={t('referrals.stat.failed')}
              value={formatNumber(data.stats.rewardsFailed)}
              danger={data.stats.rewardsFailed > 0}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button icon={<Users size={14} />} onClick={() => onShow('redemptions', data.id)}>
              {t('referrals.drawer.showJoined')}
            </Button>
            <Button icon={<Gift size={14} />} onClick={() => onShow('rewards', data.id)}>
              {t('referrals.drawer.showRewards')}
            </Button>
            <Button icon={<ScrollText size={14} />} onClick={() => onShow('activity', data.id)}>
              {t('referrals.drawer.showActivity')}
            </Button>
          </div>

          <dl className="divide-y divide-line rounded-lg border border-line px-3">
            <Row label={t('referrals.field.qualification')}>
              {t(`referrals.qualification.${data.qualification}`)}
            </Row>
            <Row label={t('referrals.field.requiredReferrals')}>
              {formatNumber(data.requiredReferrals)}
            </Row>
            <Row label={t('referrals.field.reward')}>
              {formatReward(data.rewardType, data.rewardAmount)}
            </Row>
            <Row label={t('referrals.field.maxRewards')}>
              {data.maxRewardsPerReferrer === null
                ? t('common.noLimit')
                : formatNumber(data.maxRewardsPerReferrer)}
            </Row>
            <Row label={t('referrals.field.startsAt')}>
              {data.startsAt ? formatDateTime(data.startsAt) : t('referrals.immediately')}
            </Row>
            <Row label={t('referrals.field.endsAt')}>
              {data.endsAt ? formatDateTime(data.endsAt) : t('referrals.neverExpires')}
            </Row>
            <Row label={t('referrals.field.codes')}>
              {t('referrals.drawer.codesActive', {
                active: formatNumber(data.stats.activeCodes),
                total: formatNumber(data.stats.codes),
              })}
            </Row>
            <Row label={t('referrals.field.createdBy')}>
              {`${data.createdBy} · ${formatDateTime(data.createdAt)}`}
            </Row>
          </dl>

          <Members
            campaign={data}
            onAdd={() => setAction('members')}
          />
        </div>
      )}

      <ConfirmDialog
        open={action !== null}
        title={action ? t(`referrals.drawer.confirm.${action}.title`) : ''}
        description={action ? t(`referrals.drawer.confirm.${action}.description`) : undefined}
        confirmLabel={action ? t(`referrals.drawer.confirm.${action}.action`) : undefined}
        destructive={action === 'archive'}
        loading={act.isPending}
        onCancel={() => setAction(null)}
        onConfirm={(reason) => {
          if (!action) return;
          if (action === 'members') {
            if (members.invalid.length > 0) {
              toast.error(
                t('referrals.editor.error.badIds', {
                  ids: members.invalid.slice(0, 3).join(', '),
                }),
              );
              return;
            }
            if (members.valid.length === 0) {
              toast.error(t('referrals.editor.error.noMembers'));
              return;
            }
          }
          act.mutate({ kind: action, reason });
        }}
      >
        {action === 'members' && (
          <Field
            label={t('referrals.editor.members')}
            hint={t('referrals.editor.membersHint', {
              count: members.valid.length,
              max: 1000,
            })}
          >
            <Textarea
              rows={4}
              className="font-mono text-xs"
              value={memberText}
              onChange={(event) => setMemberText(event.target.value)}
              placeholder={t('referrals.editor.membersPlaceholder')}
            />
          </Field>
        )}
      </ConfirmDialog>
    </Drawer>
  );
}

function Stat({
  label,
  value,
  danger = false,
}: {
  label: string;
  value: string;
  danger?: boolean;
}) {
  return (
    <div
      className={
        danger
          ? 'rounded-lg border border-danger/30 bg-danger-soft px-3 py-2'
          : 'rounded-lg border border-line bg-raised px-3 py-2'
      }
    >
      <p className="truncate text-[11px] text-ink-2">{label}</p>
      <p className="tnum mt-0.5 text-base font-semibold text-ink">{value}</p>
    </div>
  );
}

/**
 * Everyone holding a code for this campaign, and how far each has got. For a
 * campaign open to everyone this only lists the accounts that have opened
 * their codes screen — codes are minted then, not in bulk.
 */
function Members({
  campaign,
  onAdd,
}: {
  campaign: ReferralCampaign;
  onAdd: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [switching, setSwitching] = useState<ReferralCodeRow | null>(null);

  const codes = useQuery({
    queryKey: ['referrals', 'codes', { campaignId: campaign.id, page }],
    queryFn: () =>
      api.get<Paginated<ReferralCodeRow>>('/api/referrals/codes', {
        campaignId: campaign.id,
        page,
        limit: 10,
      }),
  });

  const toggle = useMutation({
    mutationFn: ({ code, reason }: { code: ReferralCodeRow; reason: string }) =>
      api.post<ReferralCodeRow>(
        `/api/referrals/codes/${code.id}/${code.isActive ? 'disable' : 'enable'}`,
        { reason },
      ),
    onSuccess: (saved) => {
      toast.success(t(saved.isActive ? 'referrals.code.enabled' : 'referrals.code.disabled'));
      setSwitching(null);
      void queryClient.invalidateQueries({ queryKey: ['referrals'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <section className="space-y-2 border-t border-line pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t(campaign.audience === 'users' ? 'referrals.drawer.members' : 'referrals.drawer.holders')}
        </h3>
        {campaign.audience === 'users' && (
          <Button icon={<UserPlus size={14} />} onClick={onAdd}>
            {t('referrals.drawer.addMembers')}
          </Button>
        )}
      </div>
      {campaign.audience === 'all' && (
        <p className="text-xs text-ink-3">{t('referrals.drawer.holdersHint')}</p>
      )}

      {codes.isError && <ErrorNote error={codes.error} />}
      {codes.isPending ? (
        <Skeleton className="h-40" />
      ) : (codes.data?.items.length ?? 0) === 0 ? (
        <EmptyState title={t('referrals.drawer.noCodes')} />
      ) : (
        <div className="rounded-lg border border-line">
          <ul className="divide-y divide-line">
            {codes.data?.items.map((code) => (
              <li key={code.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5">
                <code className="font-mono text-sm font-semibold tracking-wider text-ink">
                  {code.code}
                </code>
                {!code.isActive && <Badge tone="danger">{t('referrals.code.off')}</Badge>}
                <UserRef id={code.userId} />
                <span className="tnum text-xs text-ink-2">
                  {t('referrals.code.progress', {
                    qualified: code.qualified,
                    pending: code.pending,
                  })}
                </span>
                <Button
                  variant={code.isActive ? 'ghost' : 'secondary'}
                  className="ml-auto text-xs"
                  icon={code.isActive ? <PowerOff size={13} /> : <Power size={13} />}
                  onClick={() => setSwitching(code)}
                >
                  {t(code.isActive ? 'referrals.code.disable' : 'referrals.code.enable')}
                </Button>
              </li>
            ))}
          </ul>
          <Pagination
            page={codes.data?.page ?? 1}
            totalPages={codes.data?.totalPages ?? 1}
            total={codes.data?.total ?? 0}
            onChange={setPage}
          />
        </div>
      )}

      <ConfirmDialog
        open={switching !== null}
        title={
          switching
            ? t(switching.isActive ? 'referrals.code.confirmDisable' : 'referrals.code.confirmEnable', {
                code: switching.code,
              })
            : ''
        }
        description={
          switching
            ? t(
                switching.isActive
                  ? 'referrals.code.confirmDisableDescription'
                  : 'referrals.code.confirmEnableDescription',
              )
            : undefined
        }
        destructive={switching?.isActive ?? false}
        loading={toggle.isPending}
        onCancel={() => setSwitching(null)}
        onConfirm={(reason) => switching && toggle.mutate({ code: switching, reason })}
      />
    </section>
  );
}
